import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, Stepper, Step, StepLabel, Chip, Divider, CircularProgress,
  Alert, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { approveBasket, rejectBasket } from '../../store/optionsSlice';
import { SignalData, BasketData, SafetyGate } from '../../store/optionsSlice';
import toast from 'react-hot-toast';
import { useAppTheme } from '../../hooks/useAppTheme';

const STEPS = ['Review Signal', 'Confirm Legs', 'Safety Check', 'Approve'];

interface Props {
  open: boolean;
  onClose: () => void;
  signal: SignalData;
  basket: BasketData;
  gates: SafetyGate[];
}

const ExecutionModal: React.FC<Props> = ({ open, onClose, signal, basket, gates }) => {
  const { C } = useAppTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blockingFailed = gates.filter(g => g.blocking && !g.pass);
  const canApprove = blockingFailed.length === 0;

  const handleNext = () => {
    if (step < 3) setStep(s => s + 1);
  };

  const handleApprove = async () => {
    setLoading(true);
    setError(null);
    try {
      await dispatch(approveBasket(basket.id)).unwrap();
      setDone(true);
      toast.success('Basket approved and orders placed!');
      setTimeout(onClose, 2000);
    } catch (e: any) {
      setError(e.message || 'Execution failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    await dispatch(rejectBasket({ basketId: basket.id, reason: 'User rejected' }));
    toast('Signal rejected', { icon: '✗' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Execute Options Basket</Typography>
        <Stepper activeStep={step} sx={{ mt: 1.5 }} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}><StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.7rem' } }}>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </DialogTitle>

      <DialogContent dividers>
        {done ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: C.bull, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Orders Placed Successfully!</Typography>
          </Box>
        ) : (
          <>
            {/* Step 0: Review Signal */}
            {step === 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Signal Summary</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <InfoRow label="Direction" value={signal.direction} color={signal.direction === 'LONG' ? C.bull : C.bear} />
                  <InfoRow label="Score" value={`${signal.score.toFixed(0)}/100`} color={signal.score >= 85 ? C.bull : signal.score >= 70 ? C.warn : C.bear} />
                  <InfoRow label="Decision" value={signal.regimeTag || '—'} />
                  <InfoRow label="Strategy" value={signal.strategyRecommendation || '—'} />
                </Box>
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                    {signal.reason?.split('\n').slice(0, 6).join('\n')}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Step 1: Confirm Legs */}
            {step === 1 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {basket.strategy} — {basket.legs.length} Legs
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Action', 'Strike', 'Type', 'Expiry', 'Qty', 'LTP', 'Cost'].map(h => (
                        <TableCell key={h} sx={{ fontSize: '0.7rem', fontWeight: 700, py: 0.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...basket.legs].sort((a, b) => Number(b.strike) - Number(a.strike)).map((leg, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip size="small" label={leg.action}
                            sx={{ bgcolor: leg.action === 'BUY' ? C.bullMuted : C.bearMuted,
                              color: leg.action === 'BUY' ? C.bull : C.bear,
                              fontSize: '0.65rem', fontWeight: 700, height: 18 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{leg.strike}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{leg.optionType}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{leg.expiry}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{leg.quantity}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>{/^[CP]-/.test(leg.symbol || '') ? '$' : '₹'}{Number(leg.ltp || 0).toFixed(/^[CP]-/.test(leg.symbol || '') ? 2 : 1)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.5, color: leg.costPerLeg >= 0 ? C.bear : C.bull }}>
                          ₹{Math.abs(leg.costPerLeg || 0).toFixed(0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1.5 }}>
                  <InfoRow label="Net Premium" value={`₹${(basket.netPremium || 0).toFixed(0)}`} />
                  <InfoRow label="Max Profit" value={`₹${(basket.maxProfit || 0).toFixed(0)}`} color={C.bull} />
                  <InfoRow label="Max Loss" value={`₹${Math.abs(basket.maxLoss || 0).toFixed(0)}`} color={C.bear} />
                  <InfoRow label="Break-evens" value={(basket.breakEvens || []).join(', ') || '—'} />
                  <InfoRow label="Margin" value={`₹${(basket.marginEstimate || 0).toFixed(0)}`} />
                </Box>
              </Box>
            )}

            {/* Step 2: Safety Gates */}
            {step === 2 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Safety Gate Results</Typography>
                {gates.map((g) => (
                  <Box key={g.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    {g.pass ? (
                      <CheckCircleIcon sx={{ color: C.bull, fontSize: 20, flexShrink: 0 }} />
                    ) : g.blocking ? (
                      <ErrorIcon sx={{ color: C.bear, fontSize: 20, flexShrink: 0 }} />
                    ) : (
                      <WarningIcon sx={{ color: C.warn, fontSize: 20, flexShrink: 0 }} />
                    )}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{g.name}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{g.message}</Typography>
                      {!g.pass && g.blocking && (
                        <Typography variant="caption" sx={{ color: C.bear, display: 'block', fontWeight: 600 }}>
                          ⛔ BLOCKING — cannot proceed
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
                {!canApprove && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {blockingFailed.length} safety gate(s) failed. Execution is blocked.
                  </Alert>
                )}
              </Box>
            )}

            {/* Step 3: Final Approval */}
            {step === 3 && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Ready to Execute {basket.strategy}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {basket.legs.length} orders will be placed at market price.
                  Net premium: <strong>₹{(basket.netPremium || 0).toFixed(0)}</strong>
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading && <LinearProgress sx={{ mb: 2 }} />}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleReject} color="error" disabled={loading || done}>Reject</Button>
        <Box sx={{ flex: 1 }} />
        {step > 0 && !done && (
          <Button onClick={() => setStep(s => s - 1)} disabled={loading}>Back</Button>
        )}
        {step < 3 && !done && (
          <Button variant="contained" onClick={handleNext}
            disabled={step === 2 && !canApprove}
            sx={{ bgcolor: C.neutral, '&:hover': { filter: 'brightness(0.9)' } }}>
            Next
          </Button>
        )}
        {step === 3 && !done && (
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={loading || !canApprove}
            sx={{ bgcolor: C.bull, color: C.onSolid, fontWeight: 800,
              '&:hover': { filter: 'brightness(0.9)' }, minWidth: 160 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Confirm & Place Basket'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const InfoRow: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

export default ExecutionModal;
